import type { LayoutNavItem, LayoutResponse, SidebarSection } from './types';

/**
 * Which backend-provided navigation entries to render. Ported from portal-ui-lib's
 * `LayoutState`, without the feature registry.
 *
 * The backend already filters the layout by app membership, entitlements and permissions. What
 * is left for the frontend is "does this app have a page at that route?" — Angular answered it
 * from the manifests it had registered (`getAllFeatures()`), a second, hand-kept copy of the app's
 * routes. Here the answer comes from the route tree the router was built with
 * (`Object.keys(router.routesByPath)`), which cannot disagree with the pages the app has.
 *
 * Matching is per segment, not by prefix: file routes list every page, so `/leads/archive` is
 * offered only if that page exists, not merely because something under `/leads` does.
 */

function normalizePath(path: string | undefined): string {
  if (!path) {
    return '';
  }
  const trimmed = path.trim().split(/[?#]/)[0] ?? '';
  if (trimmed === '' || trimmed === '/') {
    return '';
  }
  return `/${trimmed.replace(/^\/+/, '').replace(/\/+$/, '')}`;
}

function segments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/** Does `routePath` (a router full path: `$param` segments, `$` splat) match `path`? */
function routeMatches(routePath: string, path: string): boolean {
  const pattern = segments(routePath);
  const actual = segments(path);
  for (let i = 0; i < pattern.length; i++) {
    const part = pattern[i]!;
    if (part === '$') {
      return true;
    }
    const value = actual[i];
    if (value === undefined) {
      return false;
    }
    if (!part.startsWith('$') && part !== value) {
      return false;
    }
  }
  return pattern.length === actual.length;
}

/**
 * Whether a nav item's route is a page in this app. Items without an in-app route (external
 * links, group headers) are not gated here.
 */
export function isNavigableRoute(
  itemRoute: string | undefined,
  routePaths: readonly string[],
): boolean {
  const route = normalizePath(itemRoute);
  if (!route) {
    return true;
  }
  return routePaths.some(routePath => {
    const normalized = normalizePath(routePath);
    return normalized !== '' && routeMatches(normalized, route);
  });
}

const isShown = (entry: { enabled?: boolean; visible?: boolean }) =>
  entry.enabled !== false && entry.visible !== false;

const byOrder = (a: { order?: number }, b: { order?: number }) => (a.order ?? 0) - (b.order ?? 0);

/** A sidebar entry, with whether this app has a page for it. */
export interface VisibleNavItem extends LayoutNavItem {
  /** False for an item kept by `keepUnmatched` whose page is not in this app (yet). */
  inApp: boolean;
}

export interface VisibleSidebarSection extends SidebarSection {
  /** Only the items to render, in order. */
  items: VisibleNavItem[];
}

export interface SidebarOptions {
  /**
   * Keep items whose route is not a page in this app, marked `inApp: false`, instead of dropping
   * them. For an app being migrated page by page: the backend has already filtered the layout to
   * this app's features, and the missing pages are served by the old portal meanwhile.
   */
  keepUnmatched?: boolean;
}

/**
 * The sidebar to render: shown sections, each with its shown, navigable items in order;
 * sections left empty are dropped. Children are passed through as the backend sent them, as in
 * Angular.
 */
export function visibleSidebarSections(
  layout: LayoutResponse | undefined,
  routePaths: readonly string[],
  options: SidebarOptions = {},
): VisibleSidebarSection[] {
  if (!layout) {
    return [];
  }
  return layout.layout.sidebarSections
    .filter(isShown)
    .map(section => ({
      ...section,
      items: section.items
        .filter(isShown)
        .map(item => ({ ...item, inApp: isNavigableRoute(item.route, routePaths) }))
        .filter(item => item.inApp || options.keepUnmatched === true)
        .sort(byOrder),
    }))
    .filter(section => section.items.length > 0)
    .sort(byOrder);
}

/**
 * Header entries: `headerItems` followed by the user dropdown's items, shown ones in order.
 * Not route-gated, as in Angular — they are app-agnostic chrome (profile, logout).
 */
export function visibleHeaderItems(layout: LayoutResponse | undefined): LayoutNavItem[] {
  if (!layout) {
    return [];
  }
  return [...layout.layout.headerItems, ...(layout.layout.headerConfig?.userDropdown?.items ?? [])]
    .filter(isShown)
    .sort(byOrder);
}
